import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { isFiscalSetorial } from '../utils/dataIsolation';
import API_CONFIG from '../config/api';
import { apiFetch, getToken } from '../api/client';

const ACCOUNTABILITY_TYPES = [
  { id: 'SIPEF_0530', label: 'SIPEF 0530' },
  { id: 'SIPEF_1200', label: 'SIPEF 1200' },
  { id: 'KIT_CONTABIL', label: 'Kit Contábil' },
  { id: 'PJ_TERCEIROS', label: 'PJ Terceiros' },
  { id: 'PJ_MEDICOS', label: 'PJ Médicos' },
];

const STATUS_OPTIONS = [
  { value: 'EM_ABERTO', label: 'Em aberto' },
  { value: 'EM_PREPARO', label: 'Em preparo' },
  { value: 'ENVIADO', label: 'Enviado' },
  { value: 'APROVADO', label: 'Aprovado' },
  { value: 'ATRASADO', label: 'Atrasado' },
];

// Configuração das colunas por tipo de prestação (conforme planilhas oficiais)
const TABLE_DEFINITIONS = {
  SIPEF_0530: [
    { field: 'registro', label: 'Registro', type: 'text', required: true },
    { field: 'nome', label: 'Nome (Geral)', type: 'text', required: true },
    { field: 'cpf', label: 'CPF (Geral)', type: 'text', required: true },
    { field: 'salarioBase', label: 'Salário/Vencimento Base', type: 'currency', align: 'right' },
    { field: 'salarioLiquido', label: 'Salário/Vencimento Líquido', type: 'currency', align: 'right' },
    { field: 'cargo', label: 'Cargo (Geral)', type: 'text' },
    { field: 'numeroNotaFiscal', label: 'Nº Nota Fiscal', type: 'text' },
    { field: 'mes', label: 'Mês (Geral)', type: 'text' },
    { field: 'dataAdmissao', label: 'Data Admissão/Início', type: 'text' },
    { field: 'razaoSocialEmpresa', label: 'Razão Social Empresa Vinculada', type: 'text' },
    { field: 'cnpjEmpresa', label: 'CNPJ Empresa Vinculada', type: 'text' },
  ],
  SIPEF_1200: [
    { field: 'reg', label: 'REG', type: 'text', required: true },
    { field: 'nomeContrato', label: 'Nome Contrato', type: 'text', required: true },
    { field: 'cnpjContrato', label: 'CNPJ Contrato', type: 'text', required: true },
    { field: 'numeroContrato', label: 'Nº Contrato', type: 'text', required: true },
    { field: 'objeto', label: 'Objeto', type: 'text' },
    { field: 'dataInicioVigencia', label: 'Data Início Vigência', type: 'text' },
    { field: 'dataFimVigencia', label: 'Data Fim Vigência', type: 'text' },
    { field: 'valor', label: 'Valor', type: 'currency', align: 'right', required: true },
    { field: 'valorParcela', label: 'Valor parcela', type: 'currency', align: 'right' },
    { field: 'nSerieParcela', label: 'N Série Parcela', type: 'text' },
    { field: 'caminhoContrato', label: 'Caminho Contrato', type: 'text' },
    { field: 'centroCustos', label: 'Centro de Custos', type: 'text' },
    { field: 'parcelaVariavel', label: 'Parcela Variável', type: 'text' },
    { field: 'descricaoServico', label: 'Descrição Serviço', type: 'text' },
    { field: 'nomeFantasia', label: 'Nome Fantasia', type: 'text' },
    { field: 'contratoPesquisa', label: 'Contrato Pesquisa', type: 'text' },
  ],
  KIT_CONTABIL: [
    { field: 'ordem', label: 'Ordem', type: 'int', align: 'right', required: true },
    { field: 'nomeContrato', label: 'Nome do Contrato', type: 'text', required: true },
    { field: 'numeroContrato', label: 'N° do contrato', type: 'text', required: true },
    { field: 'contratado', label: 'Contratado', type: 'text', required: true },
    { field: 'cnpjCpf', label: 'CNPJ/CPF', type: 'text', required: true },
    { field: 'objeto', label: 'Objeto', type: 'text' },
    { field: 'vigencia', label: 'Vigência', type: 'text' },
    { field: 'valorMensal', label: 'Valor Mensal', type: 'currency', align: 'right', required: true },
    { field: 'dataAssinatura', label: 'Data da Assinatura', type: 'text' },
  ],
  PJ_TERCEIROS: [
    { field: 'prestador', label: 'Prestador', type: 'text', required: true },
    { field: 'numeroContrato', label: 'Nº Contrato', type: 'text', required: true },
    { field: 'quantidadeFuncionarios', label: 'Qtd Funcionários', type: 'int', align: 'right', required: true },
    { field: 'valor', label: 'Valor (R$)', type: 'currency', align: 'right', required: true },
    {
      field: 'statusNota',
      label: 'Status da Nota',
      type: 'select',
      options: [
        { value: 'EMITIDA', label: 'Emitida' },
        { value: 'NAO_EMITIDA', label: 'Não emitida' },
      ],
      required: true,
    },
  ],
  PJ_MEDICOS: [
    { field: 'prestador', label: 'Prestador', type: 'text', required: true },
    { field: 'numeroContrato', label: 'Nº Contrato', type: 'text', required: true },
    { field: 'especialidade', label: 'Especialidade', type: 'text', required: true },
    { field: 'quantidadeMedicos', label: 'Qtd Médicos', type: 'int', align: 'right', required: true },
    { field: 'quantidadeHoras', label: 'Qtd Horas', type: 'int', align: 'right' },
    { field: 'valor', label: 'Valor (R$)', type: 'currency', align: 'right', required: true },
  ],
};

function formatCurrencyInput(value) {
  if (value === '' || value === null || value === undefined) return '';
  const numeric = String(value).replace(/\D/g, '');
  if (!numeric) return '';
  const cents = parseInt(numeric, 10);
  if (Number.isNaN(cents)) return '';
  const number = cents / 100;
  return number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseCurrencyToNumber(value) {
  if (!value) return 0;
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? 0 : n;
}

function sanitizeRowsForSave(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const clean = {};
    Object.entries(row || {}).forEach(([key, value]) => {
      const t = typeof value;
      if (value == null || t === 'string' || t === 'number' || t === 'boolean') {
        clean[key] = value;
      }
    });
    return clean;
  });
}

export default function PrestacaoContasPage() {
  const { unidadeSelecionada, user, rbacRole } = useAuth();
  const visaoFiscal = isFiscalSetorial(user, rbacRole || getUserRole(user));

  const today = new Date();
  const initialYear = today.getFullYear();
  const initialMonth = String(today.getMonth() + 1).padStart(2, '0');

  const [selectedType, setSelectedType] = useState('SIPEF_0530');
  const [competenceYear, setCompetenceYear] = useState(initialYear.toString());
  const [competenceMonth, setCompetenceMonth] = useState(initialMonth);
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState('EM_ABERTO');
  const [notes, setNotes] = useState('');

  // Histórico por tipo (carregado do backend)
  const [history, setHistory] = useState([]);

  // Linhas por tipo de prestação
  const [rowsByType, setRowsByType] = useState({
    SIPEF_0530: [],
    SIPEF_1200: [],
    KIT_CONTABIL: [],
    PJ_TERCEIROS: [],
    PJ_MEDICOS: [],
  });

  const competenceLabel = useMemo(() => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
    ];
    const idx = parseInt(competenceMonth, 10) - 1;
    const nomeMes = meses[idx] || competenceMonth;
    return `${nomeMes}/${competenceYear}`;
  }, [competenceMonth, competenceYear]);

  const currentRows = rowsByType[selectedType] || [];

  const competenceKey = useMemo(
    () => `${competenceYear}-${competenceMonth}`,
    [competenceYear, competenceMonth]
  );

  const backendUrl = API_CONFIG.BACKEND_URL;
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const importExcelInputRef = useRef(null);
  const [importingExcel, setImportingExcel] = useState(false);

  // Carregar histórico ao trocar de tipo
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await apiFetch(`/api/prestacao-contas/${selectedType}/meses`);
        if (!res.ok) throw new Error('Erro ao carregar histórico');
        const data = await res.json();
        const mapped = (data || []).map((h) => {
          const [y, m] = String(h.competence || '').split('-');
          const ano = y || '';
          const mesNum = parseInt(m || '0', 10);
          const meses = [
            'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
          ];
          const nomeMes = mesNum >= 1 && mesNum <= 12 ? meses[mesNum - 1] : h.competence;
          return {
            ...h,
            competenceLabel: ano ? `${nomeMes}/${ano}` : h.competence,
          };
        });
        setHistory(mapped);
      } catch (err) {
        console.error('Erro ao carregar histórico de prestação de contas:', err);
        setHistory([]);
      }
    };
    loadHistory();
  }, [backendUrl, selectedType]);

  // Carregar dados da competência atual
  useEffect(() => {
    const loadMes = async () => {
      try {
        const res = await apiFetch(
          `/api/prestacao-contas/${selectedType}/${competenceKey}`
        );
        if (!res.ok) throw new Error('Erro ao carregar competência');
        const data = await res.json();
        if (data && data.header) {
          const h = data.header;
          setDueDate(h.due_date || '');
          setStatus(h.effectiveStatus || h.status || 'EM_ABERTO');
          setNotes(h.notes || '');
          setRowsByType((prev) => ({
            ...prev,
            [selectedType]: data.rows || [],
          }));
        } else {
          setRowsByType((prev) => ({
            ...prev,
            [selectedType]: [],
          }));
        }
      } catch (err) {
        console.error('Erro ao carregar competência de prestação de contas:', err);
        setRowsByType((prev) => ({
          ...prev,
          [selectedType]: [],
        }));
      }
    };
    loadMes();
  }, [backendUrl, selectedType, competenceKey]);

  // Carregar anexos da competência atual
  useEffect(() => {
    const loadAttachments = async () => {
      try {
        const res = await apiFetch(
          `/api/prestacao-contas/${selectedType}/${competenceKey}/anexos`,
        );
        if (!res.ok) throw new Error('Erro ao carregar anexos');
        const data = await res.json();
        setAttachments(data || []);
      } catch (err) {
        console.error('Erro ao carregar anexos de prestação de contas:', err);
        setAttachments([]);
      }
    };
    loadAttachments();
  }, [backendUrl, selectedType, competenceKey]);

  const handleAddRow = () => {
    const defaults = selectedType === 'SIPEF_0530' ? { registro: '0530' }
      : selectedType === 'SIPEF_1200' ? { reg: '1200' }
      : selectedType === 'KIT_CONTABIL' ? { ordem: (currentRows.length + 1).toString() }
      : {};
    setRowsByType((prev) => ({
      ...prev,
      [selectedType]: [...(prev[selectedType] || []), defaults],
    }));
  };

  const handleRemoveRow = (index) => {
    setRowsByType((prev) => {
      const list = [...(prev[selectedType] || [])];
      list.splice(index, 1);
      return { ...prev, [selectedType]: list };
    });
  };

  const handleCellChange = (index, field, value) => {
    setRowsByType((prev) => {
      const list = [...(prev[selectedType] || [])];
      let newValue = value;

      const tableDef = TABLE_DEFINITIONS[selectedType];
      const col = tableDef && tableDef.find((c) => c.field === field);

      if (col?.type === 'int') {
        newValue = value === '' ? '' : String(value).replace(/\D/g, '');
      }
      if (col?.type === 'currency') {
        newValue = formatCurrencyInput(value);
      }

      list[index] = {
        ...list[index],
        [field]: newValue,
      };
      return { ...prev, [selectedType]: list };
    });
  };

  const handleSaveDraft = async (overrideStatus) => {
    try {
      const payloadStatus = overrideStatus || status;
      const cleanRows = sanitizeRowsForSave(currentRows);
      const res = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dueDate,
            status: payloadStatus,
            notes,
            rows: cleanRows,
          }),
        }
      );
      if (!res.ok) throw new Error('Erro ao salvar competência');
      const data = await res.json();
      const h = data.header;
      const updatedStatus = h.effectiveStatus || h.status || payloadStatus;
      setStatus(updatedStatus);
      const novo = {
        ...h,
        type: selectedType,
        competence: h.competence || competenceKey,
        effectiveStatus: h.effectiveStatus || h.status,
      };
      setHistory((prev) => {
        const others = prev.filter(
          (x) =>
            !(
              x.type === novo.type &&
              x.competence === novo.competence
            ),
        );
        return [...others, novo];
      });
    } catch (err) {
      console.error('Erro ao salvar prestação de contas:', err);
    }
  };

  const handleMarkAsSent = async () => {
    try {
      const newStatus = 'ENVIADO';
      await handleSaveDraft(newStatus);
    } catch (err) {
      console.error('Erro ao marcar competência como enviada:', err);
    }
  };

  const handleDuplicatePrevious = async () => {
    const month = parseInt(competenceMonth, 10);
    const year = parseInt(competenceYear, 10);
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const prevKey = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;

    try {
      const res = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}/duplicar`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error('Erro ao duplicar mês anterior');
      // Após duplicar no backend, recarregar competência atual
      const resMes = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}`
      );
      if (resMes.ok) {
        const data = await resMes.json();
        if (data && data.header) {
          setDueDate(data.header.due_date || '');
          setStatus(data.header.effectiveStatus || data.header.status || 'EM_PREPARO');
          setNotes(data.header.notes || '');
          setRowsByType((prev) => ({
            ...prev,
            [selectedType]: data.rows || [],
          }));
        }
      }
      // Atualizar histórico para incluir mês atual
      const histRes = await apiFetch(
        `/api/prestacao-contas/${selectedType}/meses`
      );
      if (histRes.ok) {
        const hist = await histRes.json();
        const mapped = (hist || []).map((h) => ({
          ...h,
          competenceLabel: h.competence,
        }));
        setHistory(mapped);
      }
    } catch (err) {
      console.error('Erro ao duplicar mês anterior de prestação de contas:', err);
    }
  };

  const handleExportExcel = () => {
    const token = getToken();
    const url = `${backendUrl}/api/prestacao-contas/${selectedType}/${competenceKey}/export-excel`;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(url, { headers })
      .then(async (res) => {
        if (!res.ok) {
          // Tentar ler erro para log, mas sem quebrar fluxo
          const data = await res.json().catch(() => ({}));
          console.error('Erro ao exportar Excel de prestação de contas:', data);
          return;
        }
        const arrayBuffer = await res.arrayBuffer();
        const blob = new Blob([arrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        const urlBlob = URL.createObjectURL(blob);
        link.href = urlBlob;
        link.download = `prestacao_contas_${selectedType}_${competenceKey}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(urlBlob);
      })
      .catch((err) => {
        console.error('Erro ao exportar Excel de prestação de contas:', err);
      });
  };

  const handleUploadAttachment = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}/anexos`,
        {
          method: 'POST',
          body: formData,
        },
      );
      if (!res.ok) throw new Error('Erro ao enviar anexo');
      // Recarregar anexos
      const listRes = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}/anexos`,
      );
      if (listRes.ok) {
        const data = await listRes.json();
        setAttachments(data || []);
      }
    } catch (err) {
      console.error('Erro ao fazer upload de anexo:', err);
    } finally {
      setUploadingAttachment(false);
      // limpar input
      event.target.value = '';
    }
  };

  const handleOpenAttachment = (id) => {
    const url = `${backendUrl}/api/prestacao-contas/anexos/${id}/view`;
    window.open(url, '_blank');
  };

  const handleDeleteAttachment = async (id) => {
    try {
      const res = await apiFetch(
        `/api/prestacao-contas/anexos/${id}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error('Erro ao deletar anexo');
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Erro ao deletar anexo:', err);
    }
  };

  const handleImportExcel = () => {
    importExcelInputRef.current?.click();
  };

  const handleImportExcelFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setImportingExcel(true);
    try {
      const res = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}/import-excel`,
        { method: 'POST', body: formData }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Erro ao importar Excel');
      }
      const data = await res.json();
      const count = data.imported ?? 0;
      const resMes = await apiFetch(
        `/api/prestacao-contas/${selectedType}/${competenceKey}`
      );
      if (resMes.ok) {
        const dataMes = await resMes.json();
        if (dataMes?.header) {
          setDueDate(dataMes.header.due_date || '');
          setStatus(dataMes.header.effectiveStatus || dataMes.header.status || 'EM_ABERTO');
          setNotes(dataMes.header.notes || '');
          setRowsByType((prev) => ({
            ...prev,
            [selectedType]: dataMes.rows || [],
          }));
        }
      }
      alert(`${count} linhas importadas com sucesso.`);
    } catch (err) {
      alert(err.message || 'Erro ao importar Excel.');
    } finally {
      setImportingExcel(false);
      e.target.value = '';
    }
  };

  const totals = useMemo(() => {
    if (selectedType === 'PJ_TERCEIROS') {
      const totalFuncionarios = currentRows.reduce(
        (sum, r) => sum + (parseInt(r.quantidadeFuncionarios || '0', 10) || 0),
        0,
      );
      const totalValor = currentRows.reduce(
        (sum, r) => sum + parseCurrencyToNumber(r.valor),
        0,
      );
      return { totalFuncionarios, totalValor };
    }
    if (selectedType === 'PJ_MEDICOS') {
      const totalMedicos = currentRows.reduce(
        (sum, r) => sum + (parseInt(r.quantidadeMedicos || '0', 10) || 0),
        0,
      );
      const totalHoras = currentRows.reduce(
        (sum, r) => sum + (parseInt(r.quantidadeHoras || '0', 10) || 0),
        0,
      );
      const totalValor = currentRows.reduce(
        (sum, r) => sum + parseCurrencyToNumber(r.valor),
        0,
      );
      return { totalMedicos, totalHoras, totalValor };
    }
    return {};
  }, [currentRows, selectedType]);

  const years = useMemo(() => {
    const base = today.getFullYear();
    return [base - 1, base, base + 1];
  }, [today]);

  const handleSelectFromHistory = (item) => {
    setSelectedType(item.type);
    const [year, month] = item.competence.split('-');
    if (year && month) {
      setCompetenceYear(year);
      setCompetenceMonth(month);
    }
    setDueDate(item.dueDate || '');
    setStatus(item.status || 'EM_ABERTO');
    setNotes(item.notes || '');
  };

  const renderTable = () => {
    const def = TABLE_DEFINITIONS[selectedType];
    if (!def) {
      return (
        <Typography variant="body2" color="text.secondary">
          Em breve: integração e tabela para o tipo selecionado.
        </Typography>
      );
    }

    return (
      <>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {def.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align || 'left'}
                    sx={{ fontWeight: 600 }}
                  >
                    {col.label}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 600, width: 80 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={def.length + 1} align="center">
                    Nenhuma linha cadastrada. Clique em &quot;Adicionar linha&quot;.
                  </TableCell>
                </TableRow>
              )}
              {currentRows.map((row, idx) => (
                <TableRow key={idx}>
                  {def.map((col) => (
                    <TableCell key={col.field} align={col.align || 'left'}>
                      {col.type === 'select' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={row[col.field] || ''}
                            onChange={(e) =>
                              handleCellChange(idx, col.field, e.target.value)
                            }
                          >
                            <MenuItem value="">Selecione</MenuItem>
                            {(col.options || []).map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          type={col.type === 'int' ? 'tel' : 'text'}
                          value={row[col.field] ?? ''}
                          onChange={(e) =>
                            handleCellChange(idx, col.field, e.target.value)
                          }
                          inputProps={
                            col.align === 'right'
                              ? { style: { textAlign: 'right' } }
                              : undefined
                          }
                        />
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Button
                      color="error"
                      size="small"
                      onClick={() => handleRemoveRow(idx)}
                    >
                      Remover
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {(selectedType === 'PJ_TERCEIROS' || selectedType === 'PJ_MEDICOS') && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: 'rgba(25,118,210,0.04)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            {selectedType === 'PJ_TERCEIROS' && (
              <>
                <Typography variant="body2">
                  <strong>Total de funcionários:</strong>{' '}
                  {totals.totalFuncionarios || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Total de valores:</strong>{' '}
                  {`R$ ${(totals.totalValor || 0).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </Typography>
              </>
            )}
            {selectedType === 'PJ_MEDICOS' && (
              <>
                <Typography variant="body2">
                  <strong>Total de médicos:</strong>{' '}
                  {totals.totalMedicos || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Total de horas:</strong>{' '}
                  {totals.totalHoras || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Total de valores:</strong>{' '}
                  {`R$ ${(totals.totalValor || 0).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </Typography>
              </>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ p: 2, minWidth: 0, maxWidth: '100%' }}>
      <VisaoRestritaBanner />
      <Grid container spacing={2}>
        {/* Cabeçalho da competência */}
        <Grid item xs={12} md={9}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Prestação de Contas — Cabeçalho do Mês
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Competência - Mês</InputLabel>
                    <Select
                      label="Competência - Mês"
                      value={competenceMonth}
                      onChange={(e) => setCompetenceMonth(e.target.value)}
                    >
                      <MenuItem value="01">Janeiro</MenuItem>
                      <MenuItem value="02">Fevereiro</MenuItem>
                      <MenuItem value="03">Março</MenuItem>
                      <MenuItem value="04">Abril</MenuItem>
                      <MenuItem value="05">Maio</MenuItem>
                      <MenuItem value="06">Junho</MenuItem>
                      <MenuItem value="07">Julho</MenuItem>
                      <MenuItem value="08">Agosto</MenuItem>
                      <MenuItem value="09">Setembro</MenuItem>
                      <MenuItem value="10">Outubro</MenuItem>
                      <MenuItem value="11">Novembro</MenuItem>
                      <MenuItem value="12">Dezembro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ano</InputLabel>
                    <Select
                      label="Ano"
                      value={competenceYear}
                      onChange={(e) => setCompetenceYear(e.target.value)}
                    >
                      {years.map((y) => (
                        <MenuItem key={y} value={String(y)}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Vencimento"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observações"
                    fullWidth
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Anexos
                      </Typography>
                      <input
                        id="file-prestacao-contas"
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleUploadAttachment}
                        disabled={uploadingAttachment}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        onClick={() => {
                          const input = document.getElementById('file-prestacao-contas');
                          if (input) input.click();
                        }}
                        disabled={uploadingAttachment}
                      >
                        {uploadingAttachment ? 'Enviando...' : 'Anexar arquivo'}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {attachments.map((a) => (
                        <Chip
                          key={a.id}
                          label={a.nome_arquivo}
                          size="small"
                          onClick={() => handleOpenAttachment(a.id)}
                          {...(!visaoFiscal
                            ? { onDelete: () => handleDeleteAttachment(a.id), deleteIcon: <DeleteIcon /> }
                            : {})}
                          icon={<AttachFileIcon fontSize="small" />}
                          sx={{ maxWidth: 220 }}
                        />
                      ))}
                      {attachments.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Nenhum anexo enviado.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveDraft()}
                      >
                        Salvar rascunho
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<SendIcon />}
                        onClick={handleMarkAsSent}
                      >
                        Marcar como Enviado
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleDuplicatePrevious}
                      >
                        Duplicar mês anterior
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <input
                        ref={importExcelInputRef}
                        type="file"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        onChange={handleImportExcelFileChange}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={handleImportExcel}
                        disabled={importingExcel}
                      >
                        {importingExcel ? 'Importando...' : 'Importar Excel'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                      >
                        Exportar Excel
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Histórico lateral */}
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Histórico
                </Typography>
                <HistoryIcon fontSize="small" color="action" />
              </Box>
              {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma competência salva ainda.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                  {history
                    .slice()
                    .sort((a, b) => (a.competence > b.competence ? -1 : 1))
                    .map((item) => {
                      const statusLabel =
                        STATUS_OPTIONS.find((s) => s.value === item.status)
                          ?.label || item.status;
                      return (
                        <Box
                          key={`${item.type}-${item.competence}`}
                          sx={{
                            py: 1,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSelectFromHistory(item)}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {item.competenceLabel}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {ACCOUNTABILITY_TYPES.find(
                              (t) => t.id === item.type,
                            )?.label || item.type}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mt: 0.5,
                            }}
                          >
                            <Chip
                              size="small"
                              label={statusLabel}
                              color={
                                item.status === 'APROVADO'
                                  ? 'success'
                                  : item.status === 'ENVIADO'
                                  ? 'primary'
                                  : item.status === 'ATRASADO'
                                  ? 'error'
                                  : 'default'
                              }
                            />
                            {item.dueDate && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Venc.:{' '}
                                {new Date(item.dueDate).toLocaleDateString(
                                  'pt-BR',
                                )}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs e tabela por tipo */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Tabs
            value={selectedType}
            onChange={(_, v) => setSelectedType(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {ACCOUNTABILITY_TYPES.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>

          {renderTable()}

          {/* Ações de tabela */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}
          >
            <Button variant="outlined" onClick={handleAddRow} size="small">
              Adicionar linha
            </Button>
            <Typography variant="caption" color="text.secondary">
              Dados ainda não estão integrados ao backend. Esta versão é um
              rascunho de UX preparado para integração com API e Excel.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

